import React, { useState } from "react";
import { useStore } from "../store";
import { lpaStatusStyle, auditorTypeStyle, LPA_ROUTING } from "../data/eightDData";
import { useLpas } from "../api/sitroom";
import { useT } from "../lib/i18n";

const filterPills = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "scheduled", label: "Scheduled" },
  { id: "in-progress", label: "In Progress" },
  { id: "completed", label: "Completed" },
];

const RoutingTable = () => (
  <div
    className="rounded-md p-3"
    style={{ background: "#111420", border: "1px solid rgba(255,255,255,0.12)" }}
  >
    <div
      style={{
        fontFamily: "'DM Mono', monospace",
        fontSize: 10,
        letterSpacing: "1.5px",
        color: "#c89adb",
        textTransform: "uppercase",
        marginBottom: 8,
      }}
    >
      Routing Rules — Severity × Standard → Auditor Type
    </div>
    <div className="overflow-x-auto">
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontFamily: "'DM Mono', monospace",
          fontSize: 10,
        }}
      >
        <thead>
          <tr style={{ color: "#9198b0" }}>
            <th className="text-left py-1 px-2" style={{ letterSpacing: "1px" }}>STANDARD</th>
            <th className="text-left py-1 px-2">S1 / CRITICAL</th>
            <th className="text-left py-1 px-2">S2 / HIGH</th>
            <th className="text-left py-1 px-2">S3 / MEDIUM</th>
            <th className="text-left py-1 px-2">S4 / LOW</th>
          </tr>
        </thead>
        <tbody>
          {["VDA 6.3", "VDA 6.8", "IATF 16949"].map((std) => (
            <tr key={std} style={{ borderTop: "1px solid rgba(255,255,255,0.05)", color: "#cfd1d8" }}>
              <td className="py-1.5 px-2" style={{ fontWeight: 600 }}>{std}</td>
              {["s1","s2","s3","s4"].map((sev) => {
                const t = auditorTypeStyle(LPA_ROUTING[sev].type);
                return (
                  <td key={sev} className="py-1.5 px-2">
                    <span
                      style={{
                        background: t.bg,
                        border: `1px solid ${t.border}`,
                        color: t.color,
                        padding: "1px 6px",
                        borderRadius: 3,
                        fontSize: 9,
                      }}
                    >
                      {t.label}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="mt-2" style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0" }}>
      Auditor assignments are pre-configured per customer/part number in Settings → Audit Configuration (Phase 3)
    </div>
  </div>
);

export default function LPA() {
  const openPanel = useStore((s) => s.openLpaPanel);
  const openPanelInc = useStore((s) => s.openPanel);
  const openNew = useStore((s) => s.openNewLpaModal);
  const [filter, setFilter] = useState("all");
  const [showRules, setShowRules] = useState(true);
  const { data: lpaRequests = [] } = useLpas();
  const { t } = useT();

  const filtered = lpaRequests.filter((r) => filter === "all" || r.status === filter);

  return (
    <div data-testid="lpa-view">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1
            style={{
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 800,
              fontSize: "clamp(26px, 3.4vw, 34px)",
              color: "#f0f2f8",
              letterSpacing: "-0.5px",
              lineHeight: 1.05,
            }}
            data-testid="view-title"
          >
            {t("lpa.title")}
          </h1>
          <div
            className="mt-1.5 font-dm-mono"
            style={{ fontSize: 11, color: "#c5cad8", letterSpacing: "0.4px" }}
          >
            {t("lpa.subtitle")}
          </div>
        </div>
        <button
          onClick={() => openNew()}
          className="px-3 py-2 rounded-md"
          style={{
            background: "#8e44ad",
            color: "#fff",
            fontFamily: "'Syne', system-ui, sans-serif",
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: "0.4px",
          }}
          data-testid="new-lpa-btn"
        >
          ＋ New Request
        </button>
      </div>

      <div className="mb-4">
        <button
          onClick={() => setShowRules(!showRules)}
          className="mb-2"
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 10,
            color: "#c5cad8",
            letterSpacing: "0.5px",
          }}
        >
          {showRules ? "▾" : "▸"} Routing Rules
        </button>
        {showRules && <RoutingTable />}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex gap-2 flex-wrap">
          {filterPills.map((p) => {
            const active = filter === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setFilter(p.id)}
                className="px-3 py-1.5 rounded-full"
                style={{
                  background: active ? "#8e44ad" : "transparent",
                  border: `1px solid ${active ? "#8e44ad" : "rgba(255,255,255,0.1)"}`,
                  color: active ? "#fff" : "#9aa1b3",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.4px",
                }}
                data-testid={`lpa-filter-${p.id}`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div style={{ minWidth: 1000 }}>
          <div
            className="grid items-center gap-3 px-4 py-2"
            style={{
              gridTemplateColumns: "130px 110px 130px 110px 130px 1fr 110px 130px",
              fontFamily: "'DM Mono', monospace",
              fontSize: 9,
              letterSpacing: "1.4px",
              color: "#9198b0",
              textTransform: "uppercase",
            }}
          >
            <div>Request ID</div>
            <div>Standard</div>
            <div>Source</div>
            <div>Severity</div>
            <div>Auditor Type</div>
            <div>Assigned Auditor</div>
            <div>Date</div>
            <div>Status</div>
          </div>
          {filtered.map((r) => {
            const at = auditorTypeStyle(r.auditorType);
            const st = lpaStatusStyle(r.status);
            return (
              <div
                key={r.id}
                onClick={() => openPanel(r.id)}
                className="row-hover grid items-center gap-3 px-4 py-3 cursor-pointer"
                style={{
                  gridTemplateColumns: "130px 110px 130px 110px 130px 1fr 110px 130px",
                  background: "#0d0f18",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 6,
                  marginBottom: 6,
                }}
                data-testid={`lpa-row-${r.id}`}
              >
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c89adb" }}>{r.id}</span>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#cfd1d8" }}>{r.standard}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); openPanelInc(r.sourceIncident); }}
                  style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9ec3ec", textDecoration: "underline", textAlign: "left" }}
                >
                  {r.sourceIncident}
                </button>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#cfd1d8", textTransform: "uppercase" }}>
                  {r.severity}
                </span>
                <span
                  style={{
                    background: at.bg, border: `1px solid ${at.border}`, color: at.color,
                    fontFamily: "'DM Mono', monospace", fontSize: 9, padding: "2px 7px", borderRadius: 3,
                    letterSpacing: "1px", justifySelf: "start",
                  }}
                >
                  {at.label}
                </span>
                <span className="truncate" style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#cfd1d8" }}>
                  {r.auditor}
                </span>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c5cad8" }}>{r.scheduledDate}</span>
                <span
                  style={{
                    background: st.bg, border: `1px solid ${st.border}`, color: st.color,
                    fontFamily: "'DM Mono', monospace", fontSize: 9, padding: "2px 7px", borderRadius: 3,
                    letterSpacing: "1px", justifySelf: "start",
                  }}
                >
                  {st.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
